import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import type { ApiFailureError } from '../../../lib/http/errors';
import { HOME_PATH } from '../../routes/paths';
import { useApiClient } from '../../session/client';
import { declineSchema, type DeclineResponse } from './schemas';
import { MY_INVITATIONS_QUERY_KEY } from './useMyInvitations';

export const DECLINE_PATH = '/invitations/decline';

/**
 * `POST /invitations/decline` (a bearer token in the body) and
 * `POST /invitations/{invitation_id}/decline` (no body -- the caller's own
 * verified address is the selector), mirroring `useAcceptInvitation`'s two
 * paths and for the same reason: neither route carries a tenant, because the
 * caller holds no membership to scope it by (`owner-decisions-align-57-2-02`).
 *
 * The token flow leaves nothing on screen once it succeeds, so it still
 * navigates home; the by-id flow declines one row inside a list the caller
 * is still reading, so it refetches that list instead of leaving it.
 */
export type DeclineInvitationInput = { token: string } | { invitationId: string };

export function useDeclineInvitation(): UseMutationResult<
  DeclineResponse,
  ApiFailureError,
  DeclineInvitationInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<DeclineResponse, ApiFailureError, DeclineInvitationInput>({
    // Retaining mutation variables would leak the bearer token beyond this screen.
    gcTime: 0,
    mutationFn: (input) =>
      'token' in input
        ? client.postParsed(DECLINE_PATH, { token: input.token }, declineSchema)
        : client.postParsed(`/invitations/${input.invitationId}/decline`, undefined, declineSchema),
    onSuccess: async (data, variables) => {
      if (data !== null) return;
      if ('token' in variables) {
        await navigate({ to: HOME_PATH });
      } else {
        await queryClient.invalidateQueries({ queryKey: MY_INVITATIONS_QUERY_KEY });
      }
    },
  });
}
